import { Autocomplete, Box, FormControl, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ButtonComponent, colors, FunctionsEnum, TextFieldComponent } from '@watter/shared-components';
import { Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { GetCountries, GetState } from 'react-country-state-city';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../custom_hooks/permissions';
import { ADD_CUSTOMER } from '../../graphql/mutation/addCustomer/addCustomerMutation';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { NavigationConfig } from '../../utils/navigation-constants';
import { MainWrapper } from '../common-styles';
import AddUserValidationSchema from './add-user-validation-schema';
import { IFormObj } from './type';

interface UpdateInstallerProps {
    onClose: () => void;
    fetchMangeInstallerData: () => void;
    open: boolean;
}

export const AddCustomer: React.FC<UpdateInstallerProps> = ({ onClose, fetchMangeInstallerData, open }) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [countriesList, setCountriesList] = useState<any[]>([]);
    const [stateList, setStateList] = useState([]);
    const [selectedCountryId, setCountryId] = useState(0);
    const [selectedCountryPhoneCode, setCountryPhoneCode] = useState('');
    const [selectedStateId, setStateId] = useState(0);
    const [userCountryCode, setUserCountryCode] = useState('');
    const [userStateCode, setUserStateCode] = useState('');
    const [formErrors, setFormErrors]: any = useState(null);

    useEffect(() => {
        if (scrollRef?.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [open]);

    const [initialForm, setInitialForm] = useState<IFormObj>({
        firstName: '',
        lastName: '',
        email: '',
        mobile_number: '',
        street: '',
        city: '',
        zipCode: '',
        state: '',
        mobileNo: '',
        country: '',
    });
    const { canCreate, canView } = usePermissions();
    const navigate = useNavigate();
    React.useEffect(() => {
        GetCountries()?.then((result: any) => {
            const filteredCountries: any = [];
            result.forEach((ct: any) => {
                if (ct.name === 'India' || ct.name === 'United States') {
                    filteredCountries.push({ ...ct, phone_code: `+${ct.phone_code}` });
                }
                setCountriesList(filteredCountries);
            });
        });
    }, []);
    React.useEffect(() => {
        const data: any = countriesList.filter((i: any) => i.iso2 === userCountryCode);
        setCountryId(data[0]?.id);
        setCountryPhoneCode(data[0]?.phone_code);
        setStateList([]);
        setStateId(0);
        setInitialForm({ ...initialForm, state: '' });
        GetState(data[0]?.id)?.then((result: any) => {
            setStateList(result);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCountryCode, countriesList]);

    React.useEffect(() => {
        const data: any = stateList.filter((i: any) => i.state_code === userStateCode);
        setStateId(data[0]?.id);
        setInitialForm({ ...initialForm, state: data[0]?.name });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateList, userStateCode]);

    const getCountryCode = () => {
        const country: any = countriesList.filter((i: any) => i.id === selectedCountryId);
        return country[0]?.iso2;
    };
    const getStateCode = () => {
        const state: any = stateList.filter((i: any) => i.id === selectedStateId);
        return state[0]?.state_code;
    };
    const isValidForm = () => {
        const error: any = {};
        if (!selectedCountryId) {
            error.country = t('select_country');
        }
        setFormErrors(error);
        return Object.keys(error).length === 0;
    };

    const addCustomerAPI = async (formData: IFormObj) => {
        try {
            dispatch(showLoader());

            const input: any = {
                user_addresses: {
                    zip_code: formData?.zipCode,
                    street: formData?.street,
                    state: getStateCode() ? getStateCode().toString() : '',
                    country: getCountryCode() ? getCountryCode().toString() : '',
                    city: formData?.city,
                    address_type: 1,
                },
                phone_number: (selectedCountryPhoneCode || '') + (formData?.mobileNo || ''),
                last_name: formData?.lastName,
                first_name: formData?.firstName,
                email: formData?.email,
                application_type: 1,
                role_id: 1,
            };

            const { data } = await client.mutate({
                mutation: ADD_CUSTOMER,
                variables: { input },
            });

            dispatch(
                openToast({
                    show: true,
                    message: data?.addUser?.message || t('customer_create_success'),
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );

            fetchMangeInstallerData();
            onClose();
        } catch (err: any) {
            console.error('Add Installer failed', err);

            if (err.graphQLErrors && err.graphQLErrors[0]?.extensions?.code === '403') {
                // Handle forbidden: show message, redirect, etc.
                // console.log('Received Forbidden error:', err);
                if (canView(FunctionsEnum.MANAGE_CUSTOMERS)) {
                    navigate(NavigationConfig.ManageInstaller);
                } else {
                    navigate(NavigationConfig.Home);
                }
                onClose();
            } else {
                dispatch(
                    openToast({
                        show: true,
                        message: err?.message,
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
            }
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleCountryChange = (e: any) => {
        setCountryId(e?.id);
        GetState(e?.id).then((result: any) => {
            setStateList(result);
        });
        const data: any = countriesList.filter((i: any) => i.id === e?.id);
        setUserCountryCode(data[0]?.iso2);
        setCountryPhoneCode(data[0]?.phone_code); // to show country prefix
        // clearing address fields on country change
        initialForm.city = '';
        initialForm.street = '';
        initialForm.zipCode = '';
        initialForm.mobileNo = '';
        setUserStateCode('');
        setInitialForm({ ...initialForm, country: e?.id });
    };
    const handleStateChange = (e: any) => {
        setStateId(e?.id);
        setInitialForm({ ...initialForm, state: e?.id });
        const data: any = stateList.filter((i: any) => i.id === e?.id);
        setUserStateCode(data[0]?.state_code);
    };
    const onSubmit = async (values: IFormObj) => {
        if (isValidForm()) {
            await addCustomerAPI(values);
        }
    };

    return (
        <Box>
            <Typography px={4} pt={4} variant="h4">
                {t('add_customer')}
            </Typography>
            <MainWrapper className="detailsForm cstUpdateInstaller">
                <Box>
                    <Formik
                        initialValues={initialForm}
                        validationSchema={AddUserValidationSchema(t)}
                        onSubmit={async values => {
                            await onSubmit(values);
                        }}
                        enableReinitialize
                    >
                        {({ submitForm, resetForm, errors }) => {
                            return (
                                <Form>
                                    <Box height="calc(100dvh - 136px)" py={1.5} overflow="auto" px={4} ref={scrollRef}>
                                        <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('first_name')}
                                                        <Box component="span" color={colors.astric}>
                                                            *
                                                        </Box>
                                                    </Typography>
                                                    <TextFieldComponent
                                                        name="firstName"
                                                        placeholder={t('enter_first_name')}
                                                        onChange={(e: any) =>
                                                            setInitialForm({
                                                                ...initialForm,
                                                                firstName: e.target.value,
                                                            })
                                                        }
                                                        value={initialForm?.firstName}
                                                    />
                                                    {errors?.firstName && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.firstName}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('last_name')}
                                                        <Box component="span" color={colors.astric}>
                                                            *
                                                        </Box>
                                                    </Typography>
                                                    <TextFieldComponent
                                                        name="lastName"
                                                        placeholder={t('enter_last_name')}
                                                        onChange={(e: any) =>
                                                            setInitialForm({ ...initialForm, lastName: e.target.value })
                                                        }
                                                        value={initialForm?.lastName}
                                                    />

                                                    {errors?.lastName && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.lastName}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6 }} display="flex">
                                                <Stack width="100%">
                                                    <>
                                                        <FormControl className="cstSelectProfile cstState">
                                                            <Typography variant="subtitle1" color="info.100">
                                                                {t('country')}
                                                                <Box component="span" color={colors.astric}>
                                                                    *
                                                                </Box>
                                                            </Typography>
                                                            <Autocomplete
                                                                options={countriesList}
                                                                getOptionLabel={(option: any) => option.name || ''}
                                                                value={
                                                                    selectedCountryId
                                                                        ? countriesList.find(
                                                                              (country: any) =>
                                                                                  country.id === selectedCountryId,
                                                                          )
                                                                        : ''
                                                                }
                                                                disableClearable
                                                                onChange={(event, newValue) =>
                                                                    handleCountryChange(newValue)
                                                                }
                                                                renderInput={params => (
                                                                    <TextField
                                                                        data-testid="country-select"
                                                                        {...params}
                                                                        inputProps={{
                                                                            ...params.inputProps,
                                                                        }}
                                                                        placeholder={t('select_country')}
                                                                        InputLabelProps={{ sx: { display: 'none' } }}
                                                                    />
                                                                )}
                                                            />
                                                        </FormControl>
                                                        {formErrors?.country && (
                                                            <Typography
                                                                variant="subtitle1"
                                                                color={`${colors.error} !important`}
                                                            >
                                                                {formErrors?.country}
                                                            </Typography>
                                                        )}
                                                    </>
                                                    {errors?.country && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.country}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }} display="flex">
                                                <Stack width="100%">
                                                    <FormControl className="cstSelectProfile cstState">
                                                        <Typography variant="subtitle1" color="info.100">
                                                            {t('state')}
                                                            <Box component="span" color={colors.astric}>
                                                                *
                                                            </Box>
                                                        </Typography>
                                                        <Autocomplete
                                                            options={stateList}
                                                            getOptionLabel={(option: any) => option.name || ''} // Specify the display label
                                                            value={
                                                                selectedStateId
                                                                    ? stateList.find(
                                                                          (state: any) => state.id === selectedStateId,
                                                                      )
                                                                    : ''
                                                            }
                                                            onChange={(event, newValue) => handleStateChange(newValue)} // Handle selection
                                                            renderInput={params => (
                                                                <TextField
                                                                    {...params}
                                                                    data-testid="state-select"
                                                                    placeholder={t('select_state')}
                                                                    InputLabelProps={{ sx: { display: 'none' } }}
                                                                />
                                                            )}
                                                        />
                                                    </FormControl>

                                                    {errors?.state && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.state}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Stack className="cstVerify cstMobileNumber">
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('mobile_no')}
                                                        <Box component="span" color={colors.astric}>
                                                            *
                                                        </Box>
                                                    </Typography>

                                                    <Box display="flex" gap={1}>
                                                        {/* Country Code (Non-Editable Select) */}
                                                        <FormControl
                                                            className="cstSelectProfile cstEmail"
                                                            sx={{ width: '20%' }}
                                                        >
                                                            {/* <Select
                                                                labelId="demo-simple-select-label"
                                                                id="demo-simple-select"
                                                                label="CountryCode"
                                                                // placeholder={`${t('country_code')}`}
                                                                value={selectedCountryPhoneCode}
                                                                className="cstEditProfile cstDisableEmail"
                                                                disabled
                                                            >
                                                                {countriesList?.map((item: any) => (
                                                                    <MenuItem key={item.id} value={item.phone_code}>
                                                                        {item?.phone_code}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select> */}
                                                            <TextFieldComponent
                                                                variant="outlined"
                                                                name="phoneNumber"
                                                                type="string"
                                                                value={selectedCountryPhoneCode}
                                                                disabled
                                                            />
                                                        </FormControl>

                                                        <Stack width="80%">
                                                            <TextFieldComponent
                                                                variant="outlined"
                                                                size="small"
                                                                name="mobileNo"
                                                                type="string"
                                                                maxLength={10}
                                                                placeholder={`${t('enter_mobile_number')}`}
                                                                value={initialForm?.mobileNo}
                                                                onChange={(e: any) =>
                                                                    setInitialForm({
                                                                        ...initialForm,
                                                                        mobileNo: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        </Stack>
                                                    </Box>
                                                    {errors?.mobileNo && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.mobileNo}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>

                                            <Grid size={{ xs: 12 }}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('email')}
                                                        <Box component="span" color={colors.astric}>
                                                            *
                                                        </Box>
                                                    </Typography>
                                                    <TextFieldComponent
                                                        name="email"
                                                        // disabled
                                                        value={initialForm?.email}
                                                        onChange={(e: any) =>
                                                            setInitialForm({ ...initialForm, email: e.target.value })
                                                        }
                                                    />
                                                </Stack>

                                                {errors?.email && (
                                                    <Typography variant="subtitle1" className="errorText">
                                                        {errors?.email}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('city')}{' '}
                                                        <Box component="span" color={colors.astric}>
                                                            *
                                                        </Box>
                                                    </Typography>
                                                    <TextFieldComponent
                                                        variant="outlined"
                                                        id=""
                                                        error={false}
                                                        size="small"
                                                        isAutoFocus
                                                        name="city"
                                                        type="text"
                                                        placeholder={`${t('enter_city')}`}
                                                        onChange={(e: any) =>
                                                            setInitialForm({ ...initialForm, city: e.target.value })
                                                        }
                                                        value={initialForm.city}
                                                    />

                                                    {errors?.city && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.city}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('building_address')}{' '}
                                                        <Box component="span" color={colors.astric}>
                                                            *
                                                        </Box>
                                                    </Typography>
                                                    <TextFieldComponent
                                                        variant="outlined"
                                                        id=""
                                                        error={false}
                                                        size="small"
                                                        isAutoFocus
                                                        name="street"
                                                        type="text"
                                                        placeholder={`${t('enter_address')}`}
                                                        onChange={(e: any) =>
                                                            setInitialForm({ ...initialForm, street: e.target.value })
                                                        }
                                                        value={initialForm.street}
                                                    />

                                                    {errors?.street && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.street}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>
                                            <Grid size={12} pb={3}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('zip_code')}
                                                        <Box component="span" color={colors.astric}>
                                                            *
                                                        </Box>
                                                    </Typography>
                                                    <TextFieldComponent
                                                        variant="outlined"
                                                        id=""
                                                        error={false}
                                                        size="small"
                                                        isAutoFocus
                                                        name="zipCode"
                                                        type="text"
                                                        placeholder={`${t('enter_zip_code')}`}
                                                        onChange={(e: any) =>
                                                            setInitialForm({ ...initialForm, zipCode: e.target.value })
                                                        }
                                                        value={initialForm.zipCode}
                                                    />

                                                    {errors?.zipCode && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.zipCode}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        className="actionFooter"
                                        spacing={2}
                                        pt={2}
                                    >
                                        <ButtonComponent
                                            size="medium"
                                            variant="contained"
                                            fullWidth
                                            buttonName={t('cancel')}
                                            className="cstBtn"
                                            type="button"
                                            onClick={() => {
                                                resetForm();
                                                onClose();
                                            }}
                                        />
                                        <ButtonComponent
                                            data-testid="add-customer-button"
                                            size="medium"
                                            variant="contained"
                                            fullWidth
                                            buttonName={t('save')}
                                            className="cstBtn"
                                            type="button"
                                            onClick={submitForm}
                                            disabled={!canCreate(FunctionsEnum.MANAGE_CUSTOMERS)}
                                        />
                                    </Stack>
                                </Form>
                            );
                        }}
                    </Formik>
                </Box>
            </MainWrapper>
        </Box>
    );
};
